import React, { useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import LocationAutocomplete from './LocationAutocomplete'; 

const PLANET_ORDER = [
  "Rising Sign", "Sun", "Moon", "Mercury", "Venus", "Mars", 
  "Jupiter", "Saturn", "Uranus", "Neptune", "Pluto", "Ceres", "Chiron"
];

const SYMBOLS = {
  "Sun": "☉", "Moon": "☽", "Mercury": "☿", "Venus": "♀", "Mars": "♂", 
  "Jupiter": "♃", "Saturn": "♄", "Uranus": "♅", "Neptune": "♆", "Pluto": "♇", 
  "Ceres": "⚳", "Chiron": "⚷", "Rising Sign": "ASC"
};

const SOMATIC_MAPPING = {
  "Aries": { node: "Crown / Cranium", chakra: "Sahasrara", bija: "OM (ॐ)" },
  "Taurus": { node: "Throat / Neck (C1-C7)", chakra: "Vishuddha", bija: "HAM (हं)" },
  "Gemini": { node: "Shoulders / Arms", chakra: "Anahata", bija: "YAM (यं)" },
  "Cancer": { node: "Chest / Breasts", chakra: "Anahata", bija: "YAM (यं)" },
  "Leo": { node: "Heart / Upper Thoracic", chakra: "Anahata", bija: "YAM (यं)" },
  "Virgo": { node: "Abdomen / Digestion", chakra: "Manipura", bija: "RAM (रं)" },
  "Libra": { node: "Kidneys / Lumbar", chakra: "Svadhisthana", bija: "VAM (वं)" },
  "Scorpio": { node: "Pelvis / Sacrum", chakra: "Svadhisthana", bija: "VAM (वं)" },
  "Ophiuchus": { node: "The Spinal Engine", chakra: "Sushumna", bija: "OM (ॐ)" },
  "Sagittarius": { node: "Hips / Thighs", chakra: "Muladhara", bija: "LAM (लं)" },
  "Capricorn": { node: "Knees / Bones", chakra: "Muladhara", bija: "LAM (लं)" },
  "Aquarius": { node: "Calves / Ankles", chakra: "Muladhara", bija: "LAM (लं)" },
  "Pisces": { node: "Feet / Earth Star", chakra: "Muladhara", bija: "LAM (लं)" },
  "Unknown": { node: "Matrix Shift", chakra: "Unknown", bija: "Silence" }
};

export default function Ephemeris() {
  const today = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    start_date: today,
    days: 30,
    lat: 37.77,
    lon_geo: -122.41,
    hour_utc: 12.0 
  });

  const [ephemerisData, setEphemerisData] = useState(null);
  const [selectedPlanet, setSelectedPlanet] = useState("All");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.get('/api/ephemeris', { params: formData });
      setEphemerisData(response.data);
    } catch (err) {
      console.error("Matrix Glitch:", err);
      // Extracts the specific python error if it crashed
      const errMsg = err.response?.data?.error || "Failed to generate ephemeris data. Please check your coordinates.";
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px', color: '#c9d1d9' }}>
      <Helmet>
        <title>13-Sign Ephemeris Engine | Adonisian Astrology</title>
        <meta name="description" content="Generate true 13-sign astronomical ephemeris tables for real-time planetary tracking and somatic planning." />
      </Helmet>

      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h2 style={{ color: '#ffca28', fontSize: '2.5rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 10px 0' }}>
          True 13-Sign Ephemeris
        </h2>
        <p style={{ color: '#8b949e', fontSize: '1.1rem', maxWidth: '700px', margin: '0 auto' }}>
          Generate exact astronomical coordinates and bio-hacker somatic targets for all planetary bodies.
        </p>
      </div>

      {/* --- EPHEMERIS CONTROL PANEL --- */}
      <div style={{ background: '#0d1117', border: '1px solid #30363d', padding: '30px', borderRadius: '12px', marginBottom: '40px', boxShadow: '0 0 20px rgba(0, 212, 255, 0.05)' }}>
        <form onSubmit={handleGenerate} style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-end' }}>
          
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', color: '#8b949e', fontSize: '0.85rem', marginBottom: '8px', textTransform: 'uppercase' }}>Start Date</label>
            <input 
              type="date" 
              value={formData.start_date}
              onChange={(e) => setFormData({...formData, start_date: e.target.value})}
              required
              style={{ width: '100%', padding: '12px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '6px', colorScheme: 'dark' }}
            />
          </div>

          <div style={{ flex: '1 1 120px' }}>
            <label style={{ display: 'block', color: '#8b949e', fontSize: '0.85rem', marginBottom: '8px', textTransform: 'uppercase' }}>Interval</label>
            <select 
              value={formData.days}
              onChange={(e) => setFormData({...formData, days: parseInt(e.target.value)})}
              style={{ width: '100%', padding: '12px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '6px' }}
            >
              <option value={7}>7 Days</option>
              <option value={14}>14 Days</option>
              <option value={30}>30 Days</option>
              <option value={90}>90 Days</option>
            </select>
          </div>

          <div style={{ flex: '2 1 300px' }}>
            <label style={{ display: 'block', color: '#8b949e', fontSize: '0.85rem', marginBottom: '8px', textTransform: 'uppercase' }}>Observer Location</label>
            <LocationAutocomplete 
              onLocationCalculated={(loc) => setFormData(prev => ({
                ...prev, 
                lat: loc.lat || 37.77, 
                lon_geo: loc.lng || -122.41 
              }))} 
            />
          </div>

          <div style={{ flex: '1 1 150px' }}>
            <button 
              type="submit" 
              disabled={loading}
              style={{
                width: '100%', background: loading ? '#30363d' : '#00d4ff', color: loading ? '#8b949e' : '#05070a',
                border: 'none', padding: '13px 20px', fontSize: '1rem', fontWeight: 'bold', textTransform: 'uppercase',
                borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease'
              }}
            >
              {loading ? "Calculating..." : "Generate Data"}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div style={{ padding: '20px', background: 'rgba(255, 77, 77, 0.1)', border: '1px solid #ff4d4d', color: '#ff4d4d', borderRadius: '8px', textAlign: 'center', marginBottom: '40px' }}>
          {error}
        </div>
      )}

      {/* --- THE DATA TABLE --- */}
      {ephemerisData && ephemerisData.length > 0 && (
        <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: '12px', overflow: 'hidden' }}>
          
          <div style={{ padding: '20px', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <h3 style={{ color: '#ffffff', margin: 0 }}>Ephemeris Data</h3>
            
            {/* PLANET FILTER DROPDOWN */}
            <select 
              value={selectedPlanet}
              onChange={(e) => setSelectedPlanet(e.target.value)}
              style={{ padding: '10px 15px', background: '#05070a', border: '1px solid #ffca28', color: '#ffca28', borderRadius: '6px', fontWeight: 'bold' }}
            >
              <option value="All">All 13 Bodies (Global View)</option>
              {PLANET_ORDER.map(p => <option key={p} value={p}>{p} (Bio-Hacker View)</option>)}
            </select>
          </div>

          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: selectedPlanet === "All" ? '1200px' : '800px' }}>
              <thead>
                <tr style={{ background: '#161b22', borderBottom: '2px solid #30363d' }}>
                  <th style={{ padding: '15px', color: '#00d4ff', fontWeight: 'bold', textTransform: 'uppercase', position: 'sticky', left: 0, background: '#161b22', zIndex: 2, borderRight: '1px solid #30363d' }}>
                    Date (UT)
                  </th>
                  
                  {/* DYNAMIC COLUMNS based on filter */}
                  {selectedPlanet === "All" ? (
                    PLANET_ORDER.map(planet => (
                      <th key={planet} style={{ padding: '15px', color: '#8b949e', fontWeight: 'normal', whiteSpace: 'nowrap' }}>
                        <span style={{ color: '#ffca28', marginRight: '5px', fontSize: '1.2rem' }}>{SYMBOLS[planet]}</span>
                        {planet}
                      </th>
                    ))
                  ) : (
                    <>
                      <th style={{ padding: '15px', color: '#ffca28' }}>Exact Placement</th>
                      <th style={{ padding: '15px', color: '#8b949e' }}>Physical Joint / Node</th>
                      <th style={{ padding: '15px', color: '#8b949e' }}>Active Chakra</th>
                      <th style={{ padding: '15px', color: '#8b949e' }}>Bija Mantra</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {ephemerisData.map((dayData, index) => {
                  const bg = index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)';
                  const stickyBg = index % 2 === 0 ? '#0d1117' : '#10141a';

                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #21262d', background: bg }}>
                      <td style={{ padding: '12px 15px', color: '#ffffff', position: 'sticky', left: 0, background: stickyBg, borderRight: '1px solid #30363d', zIndex: 1, whiteSpace: 'nowrap' }}>
                        {dayData.date}
                      </td>
                      
                      {selectedPlanet === "All" ? (
                        /* GLOBAL VIEW */
                        PLANET_ORDER.map(planet => {
                          const placement = dayData.placements[planet];
                          return (
                            <td key={planet} style={{ padding: '12px 15px', whiteSpace: 'nowrap' }}>
                              {placement ? (
                                <>
                                  <span style={{ color: '#c9d1d9', fontFamily: 'monospace', fontSize: '1.1rem', marginRight: '8px' }}>
                                    {placement.degree.toFixed(2)}°
                                  </span>
                                  <span style={{ color: '#00d4ff', fontSize: '0.85rem', textTransform: 'uppercase', fontWeight: 'bold' }}>
                                    {placement.sign}
                                  </span>
                                </>
                              ) : <span style={{ color: '#484f58' }}>N/A</span>}
                            </td>
                          );
                        })
                      ) : (
                        /* BIO-HACKER ISOLATED VIEW */
                        (() => {
                          const placement = dayData.placements[selectedPlanet];
                          if (!placement) return <td colSpan="4" style={{ padding: '12px 15px', color: '#484f58' }}>Data Unavailable</td>;
                          
                          const somaticInfo = SOMATIC_MAPPING[placement.sign] || SOMATIC_MAPPING["Unknown"];
                          return (
                            <>
                              <td style={{ padding: '12px 15px', whiteSpace: 'nowrap' }}>
                                <span style={{ color: '#ffca28', fontSize: '1.2rem', marginRight: '8px' }}>{SYMBOLS[selectedPlanet]}</span>
                                <span style={{ color: '#c9d1d9', fontFamily: 'monospace', fontSize: '1.1rem', marginRight: '8px' }}>{placement.degree.toFixed(2)}°</span>
                                <span style={{ color: '#00d4ff', fontSize: '0.85rem', textTransform: 'uppercase', fontWeight: 'bold' }}>{placement.sign}</span>
                              </td>
                              <td style={{ padding: '12px 15px', color: '#ffffff' }}>{somaticInfo.node}</td>
                              <td style={{ padding: '12px 15px', color: '#00ff41' }}>{somaticInfo.chakra}</td>
                              <td style={{ padding: '12px 15px', color: '#c9d1d9', fontStyle: 'italic' }}>{somaticInfo.bija}</td>
                            </>
                          );
                        })()
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}